import type { FC } from "react";
import { createContext, useContext, useMemo } from "react";

import { getMessageBus } from "./messageBusHandler";
import type { Envelope, IMessageBus, MessageBusHistory, PublishEnvelope, Signatures } from "./types";

type MessageBusProviderProps = {
  source: string;
};

const MessageBusContext = createContext<MessageBusProviderProps>({
  source: "jti-message-bus",
});

export const MessageBusProvider: FC<React.PropsWithChildren<MessageBusProviderProps>> = ({ source, children }) => {
  const value = useMemo(() => ({ source }), [source]);
  return <MessageBusContext.Provider value={value}>{children}</MessageBusContext.Provider>;
};

export const MessageBusConsumer = MessageBusContext.Consumer;

const createEnvelope = <Topic extends keyof Signatures>(
  data: PublishEnvelope<Signatures, Topic>,
  source: string
): PublishEnvelope<Signatures, Topic> => {
  return { ...data, headers: { source, ...data.headers } };
};

export const useMessageBus = (): IMessageBus & { source: string } => {
  const context = useContext(MessageBusContext);
  const messageBus = getMessageBus();

  return {
    publish(topic, data, options) {
      const envelope = createEnvelope(data, context.source);
      messageBus.publish(topic, envelope, options);
    },
    subscribe(request): string {
      return messageBus.subscribe(request);
    },
    unsubscribe(topic) {
      return messageBus.unsubscribe(topic);
    },
    unsubscribeToken(token: string) {
      return messageBus.unsubscribeToken(token);
    },
    getHistory(): MessageBusHistory {
      return messageBus.getHistory();
    },
    removeHistoryItem<Topic extends keyof Signatures>(channel: Topic): void {
      return messageBus.removeHistoryItem(channel);
    },
    getLastHistory<Topic extends keyof Signatures>(channel: Topic): Envelope<Signatures, Topic> | undefined {
      return messageBus.getLastHistory(channel);
    },
    reset(): void {
      messageBus.reset();
    },

    get source() {
      return context.source;
    },
  };
};
